'use client'

import { createContext, useContext, useEffect, useRef, useCallback, useState } from 'react'
import { io, Socket } from 'socket.io-client'
import { usePathname } from 'next/navigation'

// ── Types ────────────────────────────────────────────────

type EventCallback = (data: any) => void

interface SocketContextValue {
  /** Subscribe to a socket event. Returns unsubscribe function. */
  subscribe: (event: string, callback: EventCallback) => () => void
  /** True if the socket is currently connected */
  isConnected: boolean
}

const SocketContext = createContext<SocketContextValue | null>(null)

// ── Rooms to join on connect ─────────────────────────────

const ROOMS = ['queue', 'patients', 'pos', 'lab', 'inventory', 'backup', 'payments']

/**
 * During the tenant-room migration we join both the legacy global room and
 * the tenant-scoped one ('queue' and 'queue:<tenantId>'). Publishers that
 * have already been migrated emit only to the scoped room, so their events
 * no longer reach other clinics; publishers not yet migrated keep emitting
 * to the global room and keep working. Once every publisher passes a
 * tenantId, drop the legacy join below — that is what actually closes the
 * leak on this end.
 */
function roomsToJoin(tenantId: string | null): string[] {
  if (!tenantId) return ROOMS
  return [...ROOMS, ...ROOMS.map(r => `${r}:${tenantId}`)]
}

// ── Provider ─────────────────────────────────────────────

export function SocketProvider({ children }: { children: React.ReactNode }) {
  const socketRef = useRef<Socket | null>(null)
  const listenersRef = useRef<Map<string, Set<EventCallback>>>(new Map())
  const connectedRef = useRef(false)
  const [isConnected, setIsConnected] = useState(false)

  // The portal is for patients, who have no staff session: the settings route below answers them 401, and nothing this
  // socket carries is theirs. `useSocketEvent` is then a no-op there, as it has always been on a real patient's phone.
  const onPortal = usePathname().startsWith('/portal')

  // Connect once on mount (not on the portal)
  useEffect(() => {
    if (onPortal) return
    let socket: Socket | null = null

    async function connect() {
      try {
        const res = await fetch('/api/settings/websocket')
        const settings = await res.json()

        if (!settings.configured || !settings.url || !settings.apiKey) {
          console.log('[Socket] WebSocket not configured')
          return
        }

        // Namespace comes from the server so staging and production land on
        // different namespaces on the shared WS box (see WS_NAMESPACE).
        socket = io(`${settings.url}${settings.namespace || '/pms'}`, {
          auth: { apiKey: settings.apiKey },
          transports: ['websocket', 'polling'],
          upgrade: true,
          reconnection: true,
          reconnectionAttempts: 10,
          reconnectionDelay: 3000,
          reconnectionDelayMax: 15000,
          timeout: 10000,
        })

        socketRef.current = socket

        socket.on('connect', () => {
          console.log('[Socket] Connected:', socket?.id)
          connectedRef.current = true
          setIsConnected(true)
          roomsToJoin(settings.tenantId ?? null).forEach(room => socket?.emit('room:join', { room }))
        })

        socket.on('disconnect', (reason) => {
          console.log('[Socket] Disconnected:', reason)
          connectedRef.current = false
          setIsConnected(false)
        })

        socket.on('connect_error', (err) => {
          console.warn('[Socket] Connection error:', err.message)
        })

        // Wildcard-style: register listeners for ALL known events
        // When event fires, dispatch to all subscribers
        const knownEvents = [
          'queue:updated', 'patient:updated', 'prescription:new',
          'visit:order:update', 'lab:result:new', 'inventory:updated',
          'backup:progress', 'payment:confirmed', 'visit:draft:update', 'visit:draft:request'
        ]

        for (const event of knownEvents) {
          socket.on(event, (data: any) => {
            const subs = listenersRef.current.get(event)
            if (subs && subs.size > 0) {
              subs.forEach(cb => cb(data))
            }
          })
        }
      } catch (error) {
        console.error('[Socket] Failed to connect:', error)
      }
    }

    connect()

    return () => {
      if (socket) {
        socket.disconnect()
        socketRef.current = null
        connectedRef.current = false
      }
    }
  }, [onPortal])

  // Subscribe function — stable reference, components call this
  const subscribe = useCallback((event: string, callback: EventCallback) => {
    if (!listenersRef.current.has(event)) {
      listenersRef.current.set(event, new Set())
    }
    listenersRef.current.get(event)!.add(callback)

    // Return unsubscribe
    return () => {
      listenersRef.current.get(event)?.delete(callback)
    }
  }, [])

  const value = {
    subscribe,
    isConnected,
  }

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  )
}

// ── Hook: subscribe to a specific event ──────────────────

/**
 * Subscribe to a socket event. The callback fires whenever that event is received.
 * Automatically unsubscribes on unmount.
 *
 * @example
 * useSocketEvent('queue:updated', () => mutateQueue())
 * useSocketEvent('lab:result:new', (data) => console.log(data.sampleId))
 */
export function useSocketEvent(event: string, callback: EventCallback) {
  const ctx = useContext(SocketContext)
  const callbackRef = useRef(callback)

  // Keep callback ref updated (avoids re-subscribe on every render)
  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  useEffect(() => {
    if (!ctx) return

    const handler: EventCallback = (data) => callbackRef.current(data)
    const unsubscribe = ctx.subscribe(event, handler)

    return unsubscribe
  }, [ctx, event])
}

export function useSocketConnected() {
  return useContext(SocketContext)?.isConnected ?? false
}
